/**
 * Gate del motor A0 (plan 32 §2, F2): las superficies de aprendizaje A1+
 * (Conversación, Escenarios, Programa, Vocabulario, Listen, Biblioteca,
 * Actividad, Progreso) NO son para un principiante absoluto — asumen producción
 * oral / historial de conversación / vocabulario que el A0 todavía no tiene (se
 * verían vacías o rotas). Peor: dejar `/conversation` abierta era una PUERTA
 * TRASERA — un A0 que la deep-linkea entra a la charla pro y, tras unas sesiones
 * estimadas A1+, la consolidación lo promovía a A1 SALTEÁNDOSE el boss. Este
 * wrapper hace que un A0 que toca (o deep-linkea) cualquiera de esas rutas
 * rebote a `/home`, donde `HomeOrA0` le muestra el motor A0. Es el ESPEJO del
 * gate de nivel de `/home`, y tiene su defensa-en-profundidad server-side en
 * `runDimensionalConsolidation` (consolidation.ts: un A0 no se mueve por el
 * automático en ninguna dirección; sólo sale por POST /a0/graduate).
 *
 * Mientras `/state` carga (state null) deja pasar al hijo: el cache de sesión de
 * `useEnglishLevel` (stale-while-revalidate) suele traer el nivel ya resuelto
 * cuando se navega desde el motor A0, así que el rebote es inmediato y sin flash.
 * `/profile` queda DELIBERADAMENTE fuera del gate: ajustes/logout son válidos
 * para cualquier nivel, incluido A0.
 *
 * EXCEPCIÓN (plan 33): `/conversation` deja de estar locked en a0 cuando se entra
 * a una CHARLA GUIADA a0 (micro-charla o boss). Esa entrada lleva
 * `location.state.a0Mission`; con `allowA0Chat` el gate la deja pasar. La puerta
 * trasera sigue cerrada: un A0 que deep-linkea `/conversation` SIN `a0Mission`
 * (charla pro cruda) sigue rebotando — no se puede saltear el boss por ahí.
 */
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useEnglishLevel } from './useEnglishLevel.js';
import { A0_LEGACY_APP_ENABLED } from '../a0/legacy.js';

export function A0Gated({
  children,
  allowA0Chat = false,
}: {
  children: ReactNode;
  allowA0Chat?: boolean;
}) {
  const { state } = useEnglishLevel();
  const location = useLocation();
  // Con la app A0 dormida (plan 48) el A0 usa la app principal: no hay muro.
  if (A0_LEGACY_APP_ENABLED && state && state.cefrGlobal === 'A0') {
    // Charla guiada a0: la entrada trae `a0Mission` en el state (oferta del
    // cierre) o `?a0mission=` en el query (deep-link) → el A0 entra a su charla
    // guiada y podable, no a la conversación pro.
    const fromState =
      typeof (location.state as { a0Mission?: { missionId?: unknown } } | null)?.a0Mission
        ?.missionId === 'string';
    const fromQuery = new URLSearchParams(location.search).get('a0mission') != null;
    if (!allowA0Chat || (!fromState && !fromQuery)) {
      return <Navigate to="/home" replace />;
    }
  }
  return <>{children}</>;
}
